import React, { useState, useEffect } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog } from '../components/common/UI';
import { SectionHeader, ActionBtn, FormField, PageLoader, Pagination } from '../components/common/UI';
import { MdAdd, MdEdit, MdDelete, MdCheckCircle } from '../utils/icons';
import { MdCancel } from 'react-icons/md';

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', username: '', password: '', role: 'editor' });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت)

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await API.get('/auth/users');
      setUsers(data);
    } catch (err) {
      toast.error('خطا در بارگذاری کاربران');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.username || !form.password) return toast.error('همه فیلدها ضروری است');
    setSaving(true);
    try {
      const { data } = await API.post('/auth/register', form);
      toast.success('کاربر ساخته شد');
      setUsers(prev => [...prev, data.user]);
      setForm({ name: '', username: '', password: '', role: 'editor' });
      setShowForm(false);
    } catch (err) { 
      toast.error(err.response?.data?.message || 'خطا'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleUpdate = async () => {
    if (!editUser.name || !editUser.username) return toast.error('نام و نام کاربری ضروری است');
    setSaving(true);
    try {
      const { data } = await API.put(`/auth/users/${editUser._id}`, editUser);
      toast.success('کاربر بروزرسانی شد');
      setUsers(prev => prev.map(u => u._id === editUser._id ? data.user : u));
      setShowEditModal(false);
      setEditUser(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const { data } = await API.put(`/auth/users/${user._id}`, { isActive: !user.isActive });
      toast.success(user.isActive ? 'کاربر غیرفعال شد' : 'کاربر فعال شد');
      setUsers(prev => prev.map(u => u._id === user._id ? data.user : u));
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/auth/users/${deleteId}`);
      toast.success('کاربر حذف شد');
      setUsers(prev => prev.filter(u => u._id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    }
  };

  const roleLabels = { admin: 'ادمین', manager: 'مدیر', editor: 'ادیتور' };
  const roleOptions = [
    { value: 'admin', label: 'ادمین' },
    { value: 'manager', label: 'مدیر' },
    { value: 'editor', label: 'ادیتور' }
  ];

  if (loading) return <PageLoader />;

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const paged = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader 
        title="تنظیمات" 
        subtitle="مدیریت کاربران و سیستم"
        actions={<ActionBtn onClick={() => setShowForm(!showForm)} icon={<MdAdd size={16} />} label="کاربر جدید" />} 
      />

      {showForm && (
        <div className="glass-card p-5">
          <h3 className="text-gray-800 font-semibold mb-4">کاربر جدید</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="نام کامل" required>
              <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="نام کاربر" />
            </FormField>
            <FormField label="نام کاربری" required>
              <input className="form-input" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="username" />
            </FormField>
            <FormField label="پسورد" required>
              <input type="password" className="form-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="حداقل 6 کرکتر" />
            </FormField>
            <FormField label="نقش">
              <select className="form-input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </FormField>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm hover:bg-gray-200 transition-colors">انصراف</button>
            <ActionBtn onClick={handleCreate} label={saving ? 'ذخیره...' : 'ذخیره'} disabled={saving} />
          </div>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-gray-800 font-semibold">کاربران سیستم ({users.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="premium-table">
            <thead>
              <tr>
                <th>نام</th>
                <th>نام کاربری</th>
                <th>نقش</th>
                <th>وضعیت</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(u => (
                <tr key={u._id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                        {u.name?.charAt(0)}
                      </div>
                      <span className="font-medium text-gray-800">{u.name}</span>
                    </div>
                  </td>
                  <td className="text-gray-500 font-mono text-sm">{u.username}</td>
                  <td>
                    <span className={`badge ${
                      u.role === 'admin' ? 'bg-yellow-100 text-yellow-700' : 
                      u.role === 'manager' ? 'bg-blue-100 text-blue-700' : 
                      'bg-purple-100 text-purple-700'
                    } border border-gray-200`}>
                      {roleLabels[u.role]}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.isActive ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
                      {u.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => {
                          setEditUser(u);
                          setShowEditModal(true);
                        }} 
                        className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"
                        title="ویرایش"
                      >
                        <MdEdit size={16} />
                      </button>
                      <button 
                        onClick={() => handleToggleStatus(u)} 
                        className={`p-1.5 rounded-lg transition-all ${u.isActive ? 'text-red-600 hover:text-red-700 hover:bg-red-100' : 'text-green-600 hover:text-green-700 hover:bg-green-100'}`}
                        title={u.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                      >
                        {u.isActive ? <MdCancel size={16} /> : <MdCheckCircle size={16} />}
                      </button>
                      <button 
                        onClick={() => setDeleteId(u._id)} 
                        className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"
                        title="حذف"
                      >
                        <MdDelete size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="ویرایش کاربر" size="md">
        {editUser && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <FormField label="نام کامل" required>
                <input className="form-input" value={editUser.name} onChange={e => setEditUser({ ...editUser, name: e.target.value })} />
              </FormField>
              <FormField label="نام کاربری" required>
                <input className="form-input" value={editUser.username} onChange={e => setEditUser({ ...editUser, username: e.target.value })} />
              </FormField>
              <FormField label="نقش">
                <select className="form-input" value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value })}>
                  {roleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </FormField>
              <FormField label="پسورد جدید (اختیاری)">
                <input type="password" className="form-input" placeholder="برای تغییر پسورد وارد کنید" onChange={e => setEditUser({ ...editUser, password: e.target.value })} />
              </FormField>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm">انصراف</button>
              <ActionBtn onClick={handleUpdate} label={saving ? 'ذخیره...' : 'ذخیره تغییرات'} disabled={saving} />
            </div>
          </div>
        )}
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog 
        isOpen={!!deleteId} 
        onClose={() => setDeleteId(null)} 
        onConfirm={handleDelete} 
        title="حذف کاربر" 
        message="آیا مطمئن هستید؟ این کار غیرقابل بازگشت است." 
      />

      {/* Access levels */}
      <div className="glass-card p-5">
        <h3 className="text-gray-800 font-semibold mb-4">سطوح دسترسی</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { role: 'ادمین', desc: 'دسترسی کامل به تمام بخش‌ها', perms: ['مدیریت کاربران', 'همه پروژه‌ها', 'مالی', 'گزارش‌ها', 'حذف و ویرایش'], color: 'border-yellow-200 bg-yellow-100' },
            { role: 'مدیر', desc: 'مدیریت پروژه‌ها و تیم', perms: ['پروژه‌ها', 'کارمندان', 'رویدادها', 'مالی'], color: 'border-blue-200 bg-blue-100' },
            { role: 'ادیتور', desc: 'فقط ویدیوها و تسک‌های خود', perms: ['ویدیوهای خود', 'تسک‌های خود', 'چت'], color: 'border-purple-200 bg-purple-100' },
          ].map(item => (
            <div key={item.role} className={`glass-card p-4 border ${item.color}`}>
              <div className="text-lg font-bold text-gray-800 mb-1">{item.role}</div>
              <div className="text-xs text-gray-500 mb-3">{item.desc}</div>
              <ul className="space-y-1">
                {item.perms.map(p => <li key={p} className="text-xs text-gray-600 flex items-center gap-2"><span className="text-green-600">✓</span>{p}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}